import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function AddPickupLocation() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    location_name: '',
    address: '',
    latitude: '',
    longitude: '',
    contact_person: '',
    contact_phone: '',
    notes: '',
    price_one_way: '',
    price_two_way: '',
    currency: 'USD'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear success message
    if (success) {
      setSuccess('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.location_name.trim()) {
      newErrors.location_name = 'Location name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    // Validate latitude if provided
    if (formData.latitude && (isNaN(formData.latitude) || parseFloat(formData.latitude) < -90 || parseFloat(formData.latitude) > 90)) {
      newErrors.latitude = 'Latitude must be a number between -90 and 90';
    }
    
    // Validate longitude if provided
    if (formData.longitude && (isNaN(formData.longitude) || parseFloat(formData.longitude) < -180 || parseFloat(formData.longitude) > 180)) {
      newErrors.longitude = 'Longitude must be a number between -180 and 180';
    }
    
    // Validate phone number if provided
    if (formData.contact_phone && !/^[\d\s\-\+\(\)]+$/.test(formData.contact_phone)) {
      newErrors.contact_phone = 'Please enter a valid phone number';
    }
    
    // Validate prices if provided
    if (formData.price_one_way && (isNaN(formData.price_one_way) || parseFloat(formData.price_one_way) < 0)) {
      newErrors.price_one_way = 'Price must be a positive number';
    }
    
    if (formData.price_two_way && (isNaN(formData.price_two_way) || parseFloat(formData.price_two_way) < 0)) {
      newErrors.price_two_way = 'Price must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSuccess('');
    
    try {
      const pickupLocationData = {
        school_id: schoolData.id,
        location_name: formData.location_name.trim(),
        address: formData.address.trim(),
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        contact_person: formData.contact_person.trim() || null,
        contact_phone: formData.contact_phone.trim() || null,
        notes: formData.notes.trim() || null,
        price_one_way: formData.price_one_way ? parseFloat(formData.price_one_way) : null,
        price_two_way: formData.price_two_way ? parseFloat(formData.price_two_way) : null,
        currency: formData.currency || 'USD',
        is_active: true
      };
      
      const { error } = await supabase
        .from('pickup_locations')
        .insert([pickupLocationData]);
      
      if (error) {
        console.error('Error adding pickup location:', error);
        alert('Error adding pickup location: ' + error.message);
        return;
      }
      
      setSuccess('Pickup location added successfully!');
      
      // Reset form
      setFormData({
        location_name: '',
        address: '',
        latitude: '',
        longitude: '',
        contact_person: '',
        contact_phone: '',
        notes: '',
        price_one_way: '',
        price_two_way: '',
        currency: 'USD'
      });
      
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while adding the pickup location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>Add Pickup Location</h1>
        </div>
        
        <div className="content-body">
          <div className="add-school-content">
            <div className="school-form-container">
              <form onSubmit={handleSubmit} className="school-form">
                <div className="form-section">
                  <h3>Pickup Location Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="location_name">
                      Location Name <span className="asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="location_name"
                      name="location_name"
                      value={formData.location_name}
                      onChange={handleInputChange}
                      placeholder="Enter location name (e.g., Main Gate, Park Avenue)"
                      className={errors.location_name ? 'error' : ''}
                      required
                    />
                    {errors.location_name && (
                      <span className="error-text">{errors.location_name}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address">
                      Address <span className="asterisk">*</span>
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter full address of the pickup location"
                      rows="3"
                      className={errors.address ? 'error' : ''}
                      required
                    />
                    {errors.address && (
                      <span className="error-text">{errors.address}</span>
                    )}
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="latitude">Latitude (Optional)</label>
                      <input
                        type="number"
                        id="latitude"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="e.g., 40.7128"
                        step="any"
                        min="-90"
                        max="90"
                        className={errors.latitude ? 'error' : ''}
                      />
                      {errors.latitude && (
                        <span className="error-text">{errors.latitude}</span>
                      )}
                      <small style={{ color: '#6B7280', fontSize: '12px' }}>
                        For mapping purposes (between -90 and 90)
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="longitude">Longitude (Optional)</label>
                      <input
                        type="number"
                        id="longitude"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="e.g., -74.0060"
                        step="any"
                        min="-180"
                        max="180"
                        className={errors.longitude ? 'error' : ''}
                      />
                      {errors.longitude && (
                        <span className="error-text">{errors.longitude}</span>
                      )}
                      <small style={{ color: '#6B7280', fontSize: '12px' }}>
                        For mapping purposes (between -180 and 180)
                      </small>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="contact_person">Contact Person (Optional)</label>
                      <input
                        type="text"
                        id="contact_person"
                        name="contact_person"
                        value={formData.contact_person}
                        onChange={handleInputChange}
                        placeholder="Name of contact person at this location"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="contact_phone">Contact Phone (Optional)</label>
                      <input
                        type="text"
                        id="contact_phone"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleInputChange}
                        placeholder="Phone number"
                        className={errors.contact_phone ? 'error' : ''}
                      />
                      {errors.contact_phone && (
                        <span className="error-text">{errors.contact_phone}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="notes">Additional Notes (Optional)</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Enter any additional notes about this pickup location"
                      rows="4"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Pricing Information</h3>
                  
                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="currency">
                      Currency <span className="asterisk">*</span>
                    </label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#FFFFFF'
                      }}
                    >
                      <option value="USD">USD - US Dollar ($)</option>
                      <option value="PKR">PKR - Pakistani Rupee (₨)</option>
                      <option value="EUR">EUR - Euro (€)</option>
                      <option value="GBP">GBP - British Pound (£)</option>
                      <option value="INR">INR - Indian Rupee (₹)</option>
                      <option value="AED">AED - UAE Dirham (د.إ)</option>
                      <option value="SAR">SAR - Saudi Riyal (﷼)</option>
                      <option value="CAD">CAD - Canadian Dollar (C$)</option>
                      <option value="AUD">AUD - Australian Dollar (A$)</option>
                      <option value="JPY">JPY - Japanese Yen (¥)</option>
                      <option value="CNY">CNY - Chinese Yuan (¥)</option>
                    </select>
                    <small style={{ color: '#6B7280', fontSize: '12px', display: 'block', marginTop: '0.25rem' }}>
                      Select the currency for pricing
                    </small>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="price_one_way">
                        Price for 1 Way (Optional)
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6B7280',
                          fontWeight: '500',
                          fontSize: '0.9rem'
                        }}>
                          {formData.currency === 'USD' && '$'}
                          {formData.currency === 'PKR' && '₨'}
                          {formData.currency === 'EUR' && '€'}
                          {formData.currency === 'GBP' && '£'}
                          {formData.currency === 'INR' && '₹'}
                          {formData.currency === 'AED' && 'د.إ'}
                          {formData.currency === 'SAR' && '﷼'}
                          {formData.currency === 'CAD' && 'C$'}
                          {formData.currency === 'AUD' && 'A$'}
                          {formData.currency === 'JPY' && '¥'}
                          {formData.currency === 'CNY' && '¥'}
                          {!['USD', 'PKR', 'EUR', 'GBP', 'INR', 'AED', 'SAR', 'CAD', 'AUD', 'JPY', 'CNY'].includes(formData.currency) && formData.currency}
                        </span>
                        <input
                          type="number"
                          id="price_one_way"
                          name="price_one_way"
                          value={formData.price_one_way}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className={errors.price_one_way ? 'error' : ''}
                          style={{ paddingLeft: '40px' }}
                        />
                      </div>
                      {errors.price_one_way && (
                        <span className="error-text">{errors.price_one_way}</span>
                      )}
                      <small style={{ color: '#6B7280', fontSize: '12px', display: 'block', marginTop: '0.25rem' }}>
                        Enter the price for one-way transport from this location
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="price_two_way">
                        Price for 2 Way (Optional)
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6B7280',
                          fontWeight: '500',
                          fontSize: '0.9rem'
                        }}>
                          {formData.currency === 'USD' && '$'}
                          {formData.currency === 'PKR' && '₨'}
                          {formData.currency === 'EUR' && '€'}
                          {formData.currency === 'GBP' && '£'}
                          {formData.currency === 'INR' && '₹'}
                          {formData.currency === 'AED' && 'د.إ'}
                          {formData.currency === 'SAR' && '﷼'}
                          {formData.currency === 'CAD' && 'C$'}
                          {formData.currency === 'AUD' && 'A$'}
                          {formData.currency === 'JPY' && '¥'}
                          {formData.currency === 'CNY' && '¥'}
                          {!['USD', 'PKR', 'EUR', 'GBP', 'INR', 'AED', 'SAR', 'CAD', 'AUD', 'JPY', 'CNY'].includes(formData.currency) && formData.currency}
                        </span>
                        <input
                          type="number"
                          id="price_two_way"
                          name="price_two_way"
                          value={formData.price_two_way}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className={errors.price_two_way ? 'error' : ''}
                          style={{ paddingLeft: '40px' }}
                        />
                      </div>
                      {errors.price_two_way && (
                        <span className="error-text">{errors.price_two_way}</span>
                      )}
                      <small style={{ color: '#6B7280', fontSize: '12px', display: 'block', marginTop: '0.25rem' }}>
                        Enter the price for two-way transport from this location
                      </small>
                    </div>
                  </div>
                </div>
                
                {success && (
                  <div className="success-message" style={{ 
                    backgroundColor: '#D1FAE5', 
                    color: '#065F46', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '24px',
                    border: '1px solid #A7F3D0'
                  }}>
                    {success}
                  </div>
                )}
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => navigate('/school/dashboard')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn" 
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#FF8C42', color: 'white' }}
                  >
                    {isSubmitting ? 'Adding Location...' : 'Add Pickup Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddPickupLocation;

